import React, { useState, useEffect } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import PaymentForm from "./PaymentForm";
import PaymentSummary from "./PaymentSummary";
import MobileSummary from "./MobileSummary";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useAppDispatch";
import { getProduct, Product } from "../../../../services/productApiService";
import {
  confirmPayment,
  createPayment,
  savePaymentMethod,
} from "../../../../services/stripeApiService";
import {
  Gift,
  GiftProduct,
  createGift,
} from "../../../../services/giftApiService";
import { colors } from "@repo/ui";
import { RootState } from "../../../../redux/store";
import { clearCart } from "../../../../redux/slices/guestCartSlice";
import { validateTransactionToken } from "../../../../redux/slices/pricingSlice";
import { resolveDonator } from "../../../../services/donatorApiService";

import { getLastExchangeRate } from "../../../../services/exchangeRateApiService";
import i18n from "../../../../i18n";
import { clearCheckout } from "../../../../redux/slices/checkoutSlice";

const LOCALE = "modules_Guests_pages_GuestPaymentPage_GuestPaymentPage";

interface FullCartProduct {
  local: {
    productID: string;
    quantity: number;
    value: number;
  };
  api: Product;
}

const GuestPaymentPage: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const giftTableIDFromState = useAppSelector(
    (state: RootState) => state.guestGiftTable.ID,
  );
  const giftTableID = id || giftTableIDFromState;

  const cartItems = useAppSelector((state: RootState) => state.guestCart.items);
  const pricingState = useAppSelector(
    (state: RootState) => state.pricing.donationState,
  );
  const dedication = useAppSelector((state: RootState) => state.checkout);

  const [fullCartProducts, setFullCartProducts] = useState<FullCartProduct[]>(
    [],
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Estado para la tasa de cambio (por defecto 1)
  const [exchangeRate, setExchangeRate] = useState<number>(1);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  // Obtener detalles de productos
  useEffect(() => {
    const fetchProducts = async () => {
      const productDetails = await Promise.all(
        cartItems.map(async (item) => {
          const apiProduct = await getProduct(item.productID);
          return { local: item, api: apiProduct };
        }),
      );
      setFullCartProducts(productDetails);
    };

    fetchProducts();
  }, [cartItems]);

  // Obtener la tasa de cambio más reciente
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const lastRate = await getLastExchangeRate();
        setExchangeRate(lastRate.rate);
      } catch (error) {
        console.error("Error fetching exchange rate", error);
      }
    };
    fetchExchangeRate();
  }, []);

  // Determina si se usa la tasa de cambio o 1 según el idioma.
  const conversionRate = i18n.language === "es" ? 1 : exchangeRate;

  // Calcular los valores a mostrar
  const subtotalWithBrokerFee =
    (pricingState.totalCharge - pricingState.donationFeeTotal) / conversionRate;
  const serviceFee = pricingState.donationFee / conversionRate;
  const serviceFeeTax = pricingState.donationFeeTax / conversionRate;
  const finalTotal = pricingState.totalCharge;
  const finalTotalPreview = pricingState.totalCharge / conversionRate;

  const handlePayment = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!stripe || !elements) {
      console.error("Stripe.js no ha cargado aún.");
      return;
    }

    const cardElement = elements.getElement("card");
    if (!cardElement || !email || !name) {
      setErrorMessage(t("error_fill_fields"));
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setProgress(0);

    try {
      // Resolve the checkout donor without exposing the donor directory.
      const { id: donatorId } = await resolveDonator({ name, email });
      setProgress(20);

      // Crear el PaymentMethod
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name },
      });
      await savePaymentMethod({
        user_id: donatorId,
        payment_method_id: paymentMethod!.id,
      });
      if (error) {
        setErrorMessage(error.message || t("error_payment_method"));
        setLoading(false);
        return;
      }
      setProgress(40);

      // Crear PaymentIntent en el servidor y obtener clientSecret
      const paymentResponse = await createPayment({
        amount: Math.floor(finalTotal * 100),
        currency: "MXN",
        payment_type: "guest_purchase",
        user_id: donatorId,
        metadata: {
          gift_table_id: giftTableID,
          pricing_token: pricingState.token,
        },
      });
      setProgress(50);

      // Confirmar el pago en el cliente usando confirmCardPayment (maneja 3DS internamente)
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(paymentResponse.clientSecret, {
          payment_method: paymentMethod!.id,
        });

      if (confirmError) {
        setErrorMessage(confirmError.message || t("error_payment_failed"));
        setLoading(false);
        return;
      }
      if (paymentIntent.status !== "succeeded") {
        setErrorMessage(t("error_payment_failed"));
        setLoading(false);
        return;
      }
      setProgress(70);

      // Actualizar el estado del pago en el backend (simplemente actualizar Firestore)
      const finalConfirmation = await confirmPayment(
        paymentResponse.paymentIntentID,
      );
      if (finalConfirmation.status !== "succeeded") {
        setErrorMessage(t("error_payment_failed"));
        setLoading(false);
        return;
      }
      setProgress(80);

      // Continuar con la validación y creación del regalo
      await dispatch(
        validateTransactionToken({
          token: pricingState.token,
          paymentId: finalConfirmation.paymentId,
          transactionType: "donation",
        }),
      ).unwrap();
      setProgress(90);

      const mediaID = dedication.mediaID || null;
      const giftProducts: GiftProduct[] = fullCartProducts.map((item) => ({
        ID: item.local.productID,
        Quantity: item.local.quantity,
        Value: item.local.value,
      }));
      const gift: Gift = {
        DonatorID: donatorId,
        DonatorName: name,
        DonatorEmail: email,
        IsAnonymous: dedication.isAnonymous,
        Message: dedication.message,
        Products: giftProducts,
        GivenDate: new Date().toISOString(),
        Media: mediaID as string,
        GiftTableID: giftTableID,
        PaymentID: finalConfirmation.paymentId,
        PricingToken: pricingState.token,
      };

      await createGift(gift);
      setProgress(100);
      dispatch(clearCart());
      dispatch(clearCheckout());
      navigate(`/guests/checkout/success/${giftTableID}`);
    } catch (error) {
      console.error("Error procesando el pago:", error);
      setErrorMessage(t("error_processing_payment"));
    } finally {
      setLoading(false);
    }
  };

  // Deshabilitar el botón de pago si faltan campos requeridos
  const disablePayment = !name || !email || !cardComplete;

  return (
    <PageContainer startColor="white" endColor={colors.background}>
      {!isMobile && (
        <div className="py-4">
          <StepIndicator
            steps={[t("step_message"), t("step_summary"), t("step_payment")]}
            currentStep={3}
            progressPercentage={85}
          />
        </div>
      )}

      <div className="py-16 flex justify-center">
        <div className="flex flex-col md:flex-row w-full max-w-6xl bg-white shadow-md rounded-lg">
          <PaymentForm
            email={email}
            name={name}
            errorMessage={errorMessage}
            setEmail={setEmail}
            setName={setName}
            setCardComplete={setCardComplete}
            handleSubmit={handlePayment}
            onBackClick={() =>
              navigate(`/guests/checkout/preview/${giftTableID}`, {
                state: { fromPreviewPage: true },
              })
            }
          />
          {!isMobile ? (
            <PaymentSummary
              {...{
                totalPrice: subtotalWithBrokerFee,
                serviceCost: serviceFee,
                serviceTax: serviceFeeTax,
                finalTotal: finalTotalPreview,
                progress,
                loading,
                handlePayment,
                disablePayment,
              }}
            />
          ) : (
            <MobileSummary
              {...{
                totalPrice: subtotalWithBrokerFee,
                serviceCost: serviceFee,
                serviceTax: serviceFeeTax,
                finalTotal: finalTotalPreview,
                progress,
                loading,
                handlePayment,
                disablePayment,
              }}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default withTranslations(LOCALE)(GuestPaymentPage);
